import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { Alert, Badge, Button, Form, Spinner, Modal } from 'react-bootstrap';

import DesktopLayout from '../layouts/DesktopLayout';
import { AuthContext } from '../context/AuthContext';
import { BILLING_MANAGE_ROLES, BILLING_BOOKING_LINK_ROLES } from '../config/roleConfig';
import {
  billingWarningLabel,
  CUSTOMER_TYPE_LABELS,
  SOURCED_BY_LABELS,
  STANDARD_RATE_LABEL,
  ratePercent,
  vatSourceLabel,
  REVENUE_COLUMN_LABEL,
  REVENUE_FOOTING_NOTE,
  PLATFORM_VAT_NOTE,
  VAT_COLUMN_LABEL,
  GROSS_INCL_VAT_COLUMN_LABEL,
  GROSS_INCL_VAT_NOTE,
  VAT_TREATMENT_COLUMN_LABEL,
  vatTreatmentLabel,
  PLATFORM_FACTS_GROUP_LABEL,
  HOST_ME_CALC_GROUP_LABEL,
  FINALISE_BLOCKED_NOTE,
  VAT_SOURCE_COLUMN_LABEL,
  FEE_VAT_LABEL,
  BILLING_FEE_BASIS_NOTE,
  BILLING_NOT_THE_REPORT_NOTE,
  instalmentLabel,
  periodRangeLabel,
  LONG_STAY_NOTE
} from '../config/ownerBillingLabels';
import { formatBusinessMonthLatn, formatDateLatn } from '../utils/dateFormatter';
import { businessMonthKey } from '../utils/dateValueHelper';
import { moneyExact } from '../components/pricing/pricingFormat';
import { getPlatformDisplayName } from '../utils/platformUtils';
import ownerBillingApi from '../services/ownerBillingApi';
import OtaAccountsSection from '../components/ownerBilling/OtaAccountsSection';
import { buildBillingWorkbook, billingWorkbookFilename } from '../utils/ownerBillingWorkbook';
import '../styles/OwnerBillingWorksheet.css';

/**
 * Owner commission billing worksheet — the internal accounting page.
 *
 * Host Me calculates; the accountant reads this page directly, issues the invoice in
 * their own system, and types back what they issued. There is no generate button and
 * no invoice document here, by decision (tasks/internal-accounting-page.md §1).
 *
 * ── The one thing this page must never do ────────────────────────────────────
 * Let anyone edit our calculated revenue or commission. Every number in the table and
 * the totals is read-only for every role. If the accountant's invoice differs, the gap
 * is SHOWN next to it — the difference is a control, not a defect to be typed away.
 *
 * ── Draft vs final ───────────────────────────────────────────────────────────
 * Before finalisation the figures are computed live and move as the data moves; after
 * it they are read from a frozen record. The state is stated at the top of the page
 * rather than inferred, because "is this number still moving?" is the first question
 * anyone asks of a billing screen.
 */

/**
 * Money, from the SHARED helper — a column of figures read against each other has to
 * line up on the decimal, which is exactly what `moneyExact` exists for.
 *
 * `moneyExact` returns null rather than '0' so each caller decides what absence looks
 * like; here absence is an em-dash, because a null commission means the row was NOT
 * billed and rendering it as 0.00 would claim we billed zero.
 *
 * ⚠️ Imported across a feature boundary (`components/pricing/`) on purpose. There are
 * already four near-identical copies of this formatter in the codebase and
 * `reportFormat.js` records consolidating them into `utils/` as an agreed follow-up
 * pass — adding a fifth here would be the wrong side of that.
 */
const money = (value) => moneyExact(value) ?? '—';

/**
 * The last 12 months, newest first, anchored on the RIYADH day.
 *
 * ⚠️ Not on the browser's. Ops in a different timezone opening this on the 1st would
 * otherwise be offered — and defaulted to — the wrong month, on the one screen where
 * the month IS the query. `businessMonthKey` is the shared helper for that; the stepping
 * back is integer arithmetic on (year, month) rather than Date maths, because adding
 * months to a Date anchored on the 31st rolls into the month after next.
 */
const monthOptions = () => {
  const anchor = businessMonthKey(new Date());

  // ⚠️ Guarded, not trusted. If the anchor ever comes back empty this used to parse
  // NaN and emit twelve "NaN-NaN" keys, which the month formatter rejects — so the
  // picker rendered twelve identical dashes and no error anywhere. A month list is
  // not worth a broken page: fall back to UTC, which is at worst three hours out.
  const [year, month] = /^\d{4}-\d{2}$/.test(anchor)
    ? anchor.split('-').map(Number)
    : [new Date().getUTCFullYear(), new Date().getUTCMonth() + 1];

  return Array.from({ length: 12 }, (_, i) => {
    const total = year * 12 + (month - 1) - i;
    const y = Math.floor(total / 12);
    return `${String(y).padStart(4, '0')}-${String(total - y * 12 + 1).padStart(2, '0')}`;
  });
};

const SEVERITY_ORDER = { error: 0, warning: 1, info: 2 };

const OwnerBillingWorksheet = () => {
  const { user } = useContext(AuthContext);
  const canManage = BILLING_MANAGE_ROLES.includes(user?.role);
  const canOpenBooking = BILLING_BOOKING_LINK_ROLES.includes(user?.role);

  const [activeNavItem, setActiveNavItem] = useState('billing');
  const months = useMemo(monthOptions, []);

  const [owners, setOwners] = useState([]);
  const [ownersError, setOwnersError] = useState(null);
  const [ownerId, setOwnerId] = useState('');
  const [month, setMonth] = useState(months[1] || months[0]); // default: last closed month

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const [exporting, setExporting] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showFinalise, setShowFinalise] = useState(false);
  const [showReopen, setShowReopen] = useState(false);

  // ── Data ───────────────────────────────────────────────────────────────────

  useEffect(() => {
    ownerBillingApi.getOwners()
      .then(res => {
        const list = res?.data?.owners || [];
        setOwners(list);
        if (list.length === 1) setOwnerId(list[0].id);
      })
      .catch(() => setOwnersError('تعذّر تحميل قائمة الملاك'));
  }, []);

  const load = useCallback(async () => {
    if (!ownerId || !month) { setData(null); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await ownerBillingApi.getWorksheet(ownerId, month);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.error || 'تعذّر تحميل كشف الفوترة');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [ownerId, month]);

  useEffect(() => { load(); }, [load]);

  /** Every mutation reloads rather than patching local state — one read path. */
  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
      return true;
    } catch (e) {
      setError(e?.response?.data?.error || 'تعذّر إتمام العملية');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const worksheet = data?.worksheet;
  const record = data?.record;
  const isFinal = data?.state === 'final';

  /**
   * Excel handover for the accountant, built from the payload already on screen —
   * never a second query, so the file cannot disagree with what was approved.
   *
   * `exceljs` is imported lazily: it is ~1MB, and nobody who opens this page to read
   * a figure should pay for it. Webpack code-splits on the dynamic import.
   */
  const exportWorkbook = async () => {
    setExporting(true);
    setError(null);
    try {
      const ExcelJS = (await import('exceljs')).default;
      const buffer = await buildBillingWorkbook(ExcelJS, worksheet, record).xlsx.writeBuffer();
      const url = URL.createObjectURL(new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }));

      const link = document.createElement('a');
      link.href = url;
      link.download = billingWorkbookFilename(worksheet);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Billing Excel export failed:', e);
      setError('تعذّر تصدير الملف');
    } finally {
      setExporting(false);
    }
  };

  // Grouped so 30 rows sharing one problem read as one line with a count, not as 30
  // identical rows — a strip nobody scrolls is a strip nobody reads.
  const warningGroups = useMemo(() => {
    const map = new Map();
    for (const w of worksheet?.warnings || []) {
      if (!map.has(w.code)) map.set(w.code, { ...w, count: 0 });
      map.get(w.code).count += 1;
    }
    return [...map.values()].sort(
      (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]
    );
  }, [worksheet]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <DesktopLayout
      activeNavItem={activeNavItem}
      setActiveNavItem={setActiveNavItem}
      headerTitle="كشف رسوم الإدارة"
    >
      <div className="obw" dir="rtl">

        {/* ── Selector ─────────────────────────────────────────────────── */}
        <div className="obw-bar">
          <Form.Group className="obw-bar__field" controlId="obw-owner">
            <Form.Label className="obw-bar__label">المالك</Form.Label>
            <Form.Select
              value={ownerId}
              onChange={(e) => setOwnerId(e.target.value)}
              disabled={busy}
            >
              <option value="">اختر المالك…</option>
              {owners.map(o => (
                <option key={o.id} value={o.id}>
                  {o.billedName}{o.clientCode ? ` · ${o.clientCode}` : ''}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="obw-bar__field" controlId="obw-month">
            <Form.Label className="obw-bar__label">شهر الفوترة</Form.Label>
            <Form.Select value={month} onChange={(e) => setMonth(e.target.value)} disabled={busy}>
              {months.map(m => (
                <option key={m} value={m}>{formatBusinessMonthLatn(m)}</option>
              ))}
            </Form.Select>
          </Form.Group>

          {worksheet && (
            <div className="obw-bar__state">
              <Badge bg={isFinal ? 'success' : 'secondary'} className="obw-state-badge">
                {isFinal ? 'معتمد' : 'مسودة'}
              </Badge>
              <span className="obw-bar__state-note">
                {isFinal
                  ? `اعتُمد ${formatDateLatn(record?.finalisedAt)} — الأرقام ثابتة`
                  : 'الأرقام تتغيّر مع تغيّر البيانات'}
              </span>
            </div>
          )}
        </div>

        {ownersError && <Alert variant="danger" className="obw-alert">{ownersError}</Alert>}

        {error && (
          <Alert variant="danger" className="obw-alert obw-alert--retry">
            <span>{error}</span>
            <Button variant="outline-danger" size="sm" onClick={load} disabled={busy}>
              إعادة المحاولة
            </Button>
          </Alert>
        )}

        {loading && (
          <div className="obw-loading">
            <Spinner animation="border" size="sm" /> <span>جارٍ الحساب…</span>
          </div>
        )}

        {!loading && !ownerId && (
          <div className="obw-empty">اختر مالكًا وشهرًا لعرض كشف رسوم الإدارة.</div>
        )}

        {/* Owner picked, request finished, nothing to show and nothing to explain it.
            Without this the page renders blank below the selector. */}
        {!loading && ownerId && !worksheet && !error && (
          <div className="obw-empty">لا توجد بيانات لهذا المالك في هذا الشهر.</div>
        )}

        {!loading && worksheet && (
          <>
            {/* ── Customer ───────────────────────────────────────────────── */}
            <section className="obw-card obw-customer">
              <div className="obw-customer__main">
                <div className="obw-customer__name">{worksheet.owner.billedName}</div>
                <div className="obw-customer__meta">
                  {worksheet.owner.customerType
                    ? CUSTOMER_TYPE_LABELS[worksheet.owner.customerType]
                    : <span className="obw-missing">نوع العميل غير مسجّل</span>}
                  <span className="obw-sep">·</span>
                  {worksheet.owner.vatRegistered === null
                    ? <span className="obw-missing">الحالة الضريبية غير مسجّلة</span>
                    : worksheet.owner.vatRegistered
                      ? `مسجّل في ضريبة القيمة المضافة${worksheet.owner.vatNumber ? ` — ${worksheet.owner.vatNumber}` : ''}`
                      : 'غير مسجّل في ضريبة القيمة المضافة'}
                </div>
                {/* The negotiated owner-sourced rate, stated ONLY when one exists.
                    Without it a 15% line on the table below has no explanation on the
                    document the accountant bills from — the row's «من المالك» chip says
                    why the rate differs, this says what was agreed. Read-only for every
                    role here; the rate is set on the profile form, manager-only. */}
                {worksheet.owner.ownerSourcedCommissionRate != null && (
                  <div className="obw-customer__meta">
                    نسبة الحجوزات التي يوفّرها المالك:{' '}
                    <strong>{ratePercent(worksheet.owner.ownerSourcedCommissionRate)}</strong>
                    <span className="obw-sep">·</span>
                    {STANDARD_RATE_LABEL} {ratePercent(worksheet.commissionRate)}
                  </div>
                )}
              </div>
              {/* Hidden once the month is final. The customer block then shows the
                  snapshot FROZEN at finalisation, not the live profile — opening an
                  edit form seeded from an archive and saving would push last month's
                  tax identity back over the current one. */}
              {canManage && !isFinal && (
                <Button variant="outline-secondary" size="sm" onClick={() => setShowProfile(true)}>
                  تعديل الملف الضريبي
                </Button>
              )}
            </section>

            {/* ── OTA accounts + VAT history ──────────────────────────────
                Per OWNER, not per month, and live on a finalised month too — its own
                query, so it never moves with (or breaks) the worksheet above. */}
            <OtaAccountsSection ownerId={ownerId} canManage={canManage} />

            {/* ── Warnings ───────────────────────────────────────────────── */}
            {warningGroups.length > 0 && (
              <section className="obw-warnings">
                {warningGroups.map(w => (
                  <div key={w.code} className={`obw-warning obw-warning--${w.severity}`}>
                    <span className="obw-warning__text">{billingWarningLabel(w.code)}</span>
                    {w.count > 1 && <span className="obw-warning__count">{w.count}</span>}
                  </div>
                ))}
              </section>
            )}

            {/* ── Calculation ────────────────────────────────────────────── */}
            <section className="obw-card">
              {worksheet.properties.length === 0 && (
                <div className="obw-empty obw-empty--inline">لا توجد حجوزات مستحقة الفوترة في هذا الشهر.</div>
              )}

              {worksheet.properties.map(group => (
                <div key={group.propertyId || group.name} className="obw-group">
                  <div className="obw-group__head">
                    <span className="obw-group__name">{group.name}</span>
                    {group.code && <span className="obw-group__code">{group.code}</span>}
                  </div>

                  <div className="obw-table-wrap">
                    <table className="obw-table">
                      <thead>
                        {/* Two tiers. The upper row is the audit trail (consultant, round
                            4): the columns under «بيانات المنصة» are checked against the
                            platform's own statement, those under «احتساب هوست مي» against
                            the contract. Column ORDER is the whole design — no new
                            component, no redesign (tasks/vat-exclusive-fee-basis.md §7). */}
                        <tr className="obw-groups">
                          <th colSpan={3} className="obw-groups__blank" />
                          <th colSpan={4} className="obw-groups__head obw-boundary">{PLATFORM_FACTS_GROUP_LABEL}</th>
                          <th colSpan={4} className="obw-groups__head obw-boundary">{HOST_ME_CALC_GROUP_LABEL}</th>
                        </tr>
                        <tr>
                          <th>الحجز</th>
                          <th>المنصة</th>
                          <th>المغادرة</th>
                          {/* ── Platform facts ── */}
                          {/* Room revenue + the booking's VAT: the one figure that matches
                              the platform's statement line for line. */}
                          <th className="obw-num obw-boundary">{GROSS_INCL_VAT_COLUMN_LABEL} (ر.س)</th>
                          {/* The guest's VAT on the room — NOT the VAT on our fee, which is
                              in the totals block below. Its own column so the accountant
                              reads a captured figure instead of auditing a division. */}
                          <th className="obw-num">{VAT_COLUMN_LABEL} (ر.س)</th>
                          <th>{VAT_SOURCE_COLUMN_LABEL}</th>
                          {/* Kept (decided 2026-08-20). It changes nothing we charge — we
                              bill before the OTA fee — but it is the OWNER'S deductible
                              expense and his accountant needs it. */}
                          <th className="obw-num">عمولة المنصة (ر.س)</th>
                          {/* ── Host Me's calculation ── */}
                          {/* Where the VAT sat in the amount the platform sent — the
                              per-booking fact that decides whether the gross beside it is
                              the stored amount or the stored amount plus VAT. */}
                          <th className="obw-boundary">{VAT_TREATMENT_COLUMN_LABEL}</th>
                          {/* The fee is 25% of THIS column. The separate «أساس احتساب
                              الرسوم» column was retired in v1.2.17: under the VAT-exclusive
                              basis it equalled this one on every row. */}
                          <th className="obw-num">{REVENUE_COLUMN_LABEL} (ر.س)</th>
                          {/* The rate belongs beside the line, not beside the grand total:
                              a month can mix the standard rate with an owner's negotiated
                              one, and a single percentage on the total cannot describe it.
                              The accountant must not have to interpret one figure. */}
                          <th className="obw-num">النسبة</th>
                          <th className="obw-num">رسوم إدارة هوست مي (ر.س)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.rows.map(row => (
                          <tr
                            key={`${row.bookingId || row.reference}-${row.periodStart || ''}`}
                            className={row.billed ? '' : 'obw-row--unbilled'}
                          >
                            <td className="obw-ref">
                              {/* New tab, so the worksheet keeps its month and scroll position. */}
                              {canOpenBooking && row.bookingId ? (
                                <a
                                  className="obw-ref-link"
                                  href={`#/bookings/${row.bookingId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="فتح الحجز في نافذة جديدة"
                                >
                                  {row.reference}
                                </a>
                              ) : row.reference}
                              {/* An instalment says so on its own row. Without this, one
                                  booking reference appearing on two months' sheets reads
                                  as a duplicate charge — the whole reason the footnote
                                  and this chip exist. `of > 1` is the backend's own
                                  normalised answer; the page never works out whether a
                                  row is an instalment (ARCHITECTURE_PATTERNS →
                                  Backend Response Normalization). */}
                              {row.of > 1 && (
                                <>
                                  <span className="obw-instalment">
                                    {instalmentLabel(row.sequence, row.of)}
                                  </span>
                                  <span className="obw-period">
                                    {periodRangeLabel(
                                      formatDateLatn(row.periodStart),
                                      formatDateLatn(row.periodEnd)
                                    )}
                                  </span>
                                  {row.stay && (
                                    <span className="obw-stay">
                                      {`إجمالي الإقامة ${money(row.stay.revenue)}`}
                                      {` · المفوتر سابقًا ${money(row.stay.billedBefore)}`}
                                      {` · المتبقي ${money(row.stay.remaining)}`}
                                    </span>
                                  )}
                                </>
                              )}
                            </td>
                            <td>{getPlatformDisplayName(row.platform)}</td>
                            <td className="obw-day">{formatDateLatn(row.checkOut)}</td>
                            <td className="obw-num obw-boundary">{money(row.grossInclVat)}</td>
                            <td className="obw-num">{money(row.vat)}</td>
                            <td className="obw-src">{vatSourceLabel(row.vatSource, row.platform, row.vat)}</td>
                            <td className="obw-num obw-muted">{money(row.otaFee)}</td>
                            <td className="obw-src obw-boundary">{vatTreatmentLabel(row.revenueFooting, row.vatSource)}</td>
                            <td className="obw-num obw-strong">{money(row.revenue)}</td>
                            <td className="obw-num">
                              {ratePercent(row.commissionRate)}
                              {/* Only the exception is labelled. Stamping «من هوست مي» on
                                  every ordinary row would bury the one row that differs. */}
                              {row.sourcedBy === 'owner' && (
                                <span className="obw-tier">{SOURCED_BY_LABELS.owner}</span>
                              )}
                            </td>
                            <td className="obw-num obw-strong">{money(row.commission)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan={3}>مجموع الوحدة</td>
                          <td className="obw-num obw-boundary">{money(group.subtotal.grossInclVat)}</td>
                          <td className="obw-num">{money(group.subtotal.vat)}</td>
                          <td />
                          <td />
                          <td className="obw-boundary" />
                          <td className="obw-num obw-strong">{money(group.subtotal.revenue)}</td>
                          <td />
                          <td className="obw-num obw-strong">{money(group.subtotal.commission)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              ))}

              {worksheet.properties.length > 0 && (
                <div className="obw-footnote">
                  {/* Three things a reader cannot verify from the row itself. First the
                      revenue column: the Booking.com figure is 15% lower than the number
                      on their extranet, and without this line that looks like an error
                      rather than a conversion. Second, the fee is 25% of the WITH-VAT
                      column, not of the room column beside it. Third — since Airbnb began
                      reporting «صفر» for a VAT-registered host — the VAT column is what the
                      platform reported, not a statement of what the owner owes. The
                      paragraph deliberately does NOT say Airbnb amounts are «بدون ضريبة». */}
                  <p className="obw-footnote__line">
                    <strong>{REVENUE_COLUMN_LABEL}:</strong> {REVENUE_FOOTING_NOTE}
                  </p>
                  <p className="obw-footnote__line">{GROSS_INCL_VAT_NOTE}</p>
                  <p className="obw-footnote__line">{BILLING_FEE_BASIS_NOTE}</p>
                  <p className="obw-footnote__line">{PLATFORM_VAT_NOTE}</p>
                  {/* Shown only when the sheet actually holds an instalment. A permanent
                      paragraph about a rule that applies to nothing on the page is how a
                      footnote block stops being read. */}
                  {worksheet.properties.some(g => g.rows.some(r => r.of > 1)) && (
                    <p className="obw-footnote__line">{LONG_STAY_NOTE}</p>
                  )}
                </div>
              )}
            </section>

            {/* ── Totals ─────────────────────────────────────────────────── */}
            <section className="obw-card obw-totals">
              <div className="obw-totals__grid">
                {/* Same order as the table: the platform's figures, then ours. The Excel
                    export prints the same three totals, so the screen must too. */}
                <div className="obw-total">
                  <span className="obw-total__label">{GROSS_INCL_VAT_COLUMN_LABEL} (ر.س)</span>
                  <span className="obw-total__value">{money(worksheet.totals.grossInclVat)}</span>
                </div>
                <div className="obw-total">
                  <span className="obw-total__label">{VAT_COLUMN_LABEL} (ر.س)</span>
                  <span className="obw-total__value">{money(worksheet.totals.revenueVat)}</span>
                </div>
                <div className="obw-total">
                  <span className="obw-total__label">{REVENUE_COLUMN_LABEL} (ر.س)</span>
                  <span className="obw-total__value">{money(worksheet.totals.revenue)}</span>
                </div>
                <div className="obw-total">
                  {/* ⚠️ RATE-FREE, deliberately. This label used to print the statement's
                      single rate, which is wrong the moment a month mixes tiers — and it
                      sits on the page the accountant bills from. The rate now lives on
                      each line, which is the only place it can be true. */}
                  <span className="obw-total__label">رسوم إدارة هوست مي</span>
                  <span className="obw-total__value">{money(worksheet.totals.commission)}</span>
                </div>
                {record?.discount?.amount > 0 && (
                  <div className="obw-total obw-total--discount">
                    <span className="obw-total__label">خصم — {record.discount.reason}</span>
                    <span className="obw-total__value">−{money(record.discount.amount)}</span>
                  </div>
                )}
                <div className="obw-total">
                  {/* ⚠️ VAT on OUR FEE. The column of the same name in the table above is
                      the VAT the GUEST paid on the room — two different taxes, and this
                      label has to say which one it is. */}
                  <span className="obw-total__label">
                    {FEE_VAT_LABEL} ({Math.round(worksheet.vatRate * 100)}%)
                  </span>
                  <span className="obw-total__value">
                    {money(record ? record.billedTotals.vat : worksheet.totals.vat)}
                  </span>
                </div>
                <div className="obw-total obw-total--grand">
                  <span className="obw-total__label">الإجمالي (ر.س)</span>
                  <span className="obw-total__value">
                    {money(record ? record.billedTotals.total : worksheet.totals.total)}
                  </span>
                </div>
              </div>

              <div className="obw-totals__meta">
                {worksheet.totals.billedCount} من {worksheet.totals.bookingCount} حجز محتسب
                {worksheet.totals.unpricedCount > 0 && (
                  <strong className="obw-unpriced"> · {worksheet.totals.unpricedCount} بدون قيمة</strong>
                )}
              </div>

              <div className="obw-actions">
                {/* Available in BOTH states, and to the accountant: exporting a draft is
                    a legitimate way to check figures before they are settled — the file
                    says which state it came from. */}
                <Button
                  variant="outline-primary"
                  disabled={busy || exporting || worksheet.totals.bookingCount === 0}
                  onClick={exportWorkbook}
                >
                  {exporting ? 'جارٍ التصدير…' : 'تصدير Excel'}
                </Button>

                {canManage && !isFinal && (
                  <Button
                    variant="primary"
                    disabled={busy || !data?.monthComplete || worksheet.totals.bookingCount === 0}
                    onClick={() => setShowFinalise(true)}
                  >
                    اعتماد الشهر
                  </Button>
                )}
              </div>
              {!isFinal && !data?.monthComplete && (
                <div className="obw-hint">لم ينتهِ هذا الشهر بعد — يمكن الاطلاع عليه، ولا يمكن اعتماده.</div>
              )}
            </section>

            {/* ── External invoice ───────────────────────────────────────── */}
            {isFinal && (
              <ExternalInvoiceBlock
                record={record}
                busy={busy}
                canManage={canManage}
                onSave={(values) => run(() => ownerBillingApi.recordExternalInvoice(record._id, values))}
                onReopen={() => setShowReopen(true)}
              />
            )}

            <p className="obw-note">{BILLING_NOT_THE_REPORT_NOTE}</p>
          </>
        )}

        {/* ── Dialogs ──────────────────────────────────────────────────── */}
        <BillingProfileModal
          show={showProfile}
          owner={worksheet?.owner}
          busy={busy}
          onHide={() => setShowProfile(false)}
          onSave={async (profile) => {
            const ok = await run(() => ownerBillingApi.updateBillingProfile(ownerId, profile));
            if (ok) setShowProfile(false);
          }}
        />

        <FinaliseModal
          show={showFinalise}
          month={month}
          total={worksheet?.totals?.total}
          hasBlockingWarnings={warningGroups.some(w => w.severity === 'error')}
          // The gate, mirrored from the worksheet's own warnings so the modal can say WHICH
          // rows before the server refuses. The refusal itself is server-side either way.
          unresolvedRefs={(worksheet?.properties || [])
            .flatMap(g => g.rows)
            .filter(r => (r.warnings || []).some(w => w.code === 'revenue_unknown_footing'))
            .map(r => r.reference)}
          busy={busy}
          onHide={() => setShowFinalise(false)}
          onConfirm={async (discount) => {
            const ok = await run(() => ownerBillingApi.finalise({ owner: ownerId, periodMonth: month, discount }));
            if (ok) setShowFinalise(false);
          }}
        />

        <ReopenModal
          show={showReopen}
          hasInvoice={Boolean(record?.external?.invoiceNumber)}
          busy={busy}
          onHide={() => setShowReopen(false)}
          onConfirm={async (reason) => {
            const ok = await run(() => ownerBillingApi.reopen(record._id, reason));
            if (ok) setShowReopen(false);
          }}
        />
      </div>
    </DesktopLayout>
  );
};

/**
 * What the accountant issued. Three fields — number, date, total — because those are
 * the three that do a job nothing else does: find the document, date it, and check we
 * agree on the amount. Payment status is absent on purpose: collections live in their
 * system, and a flag nobody keeps current reads as an answer.
 */
const ExternalInvoiceBlock = ({ record, busy, canManage, onSave, onReopen }) => {
  const [values, setValues] = useState({
    invoiceNumber: record?.external?.invoiceNumber || '',
    invoiceDate: record?.external?.invoiceDate || '',
    issuedTotal: record?.external?.issuedTotal ?? ''
  });

  useEffect(() => {
    setValues({
      invoiceNumber: record?.external?.invoiceNumber || '',
      invoiceDate: record?.external?.invoiceDate || '',
      issuedTotal: record?.external?.issuedTotal ?? ''
    });
  }, [record]);

  const set = (key) => (e) => setValues(v => ({ ...v, [key]: e.target.value }));
  const gap = record?.discrepancy;

  return (
    <section className="obw-card obw-external">
      <h2 className="obw-card__title">الفاتورة الصادرة من المحاسب</h2>

      <div className="obw-external__fields">
        <Form.Group controlId="obw-invoice-number">
          <Form.Label className="obw-bar__label">رقم الفاتورة</Form.Label>
          <Form.Control value={values.invoiceNumber} onChange={set('invoiceNumber')} disabled={busy} />
        </Form.Group>
        <Form.Group controlId="obw-invoice-date">
          <Form.Label className="obw-bar__label">تاريخ الفاتورة</Form.Label>
          <Form.Control type="date" value={values.invoiceDate} onChange={set('invoiceDate')} disabled={busy} />
        </Form.Group>
        <Form.Group controlId="obw-invoice-total">
          <Form.Label className="obw-bar__label">إجمالي الفاتورة (ر.س)</Form.Label>
          <Form.Control
            type="number" step="0.01" min="0" inputMode="decimal"
            value={values.issuedTotal} onChange={set('issuedTotal')} disabled={busy}
          />
        </Form.Group>
        <Button variant="primary" disabled={busy} onClick={() => onSave({
          invoiceNumber: values.invoiceNumber || null,
          invoiceDate: values.invoiceDate || null,
          issuedTotal: values.issuedTotal === '' ? null : Number(values.issuedTotal)
        })}>
          حفظ
        </Button>
      </div>

      {/* The control this whole screen exists for: the gap is shown, never absorbed. */}
      {gap != null && gap !== 0 && (
        <Alert variant="warning" className="obw-gap">
          فرق بين فاتورة المحاسب وحسابنا: <strong>{money(Math.abs(gap))}</strong>{' '}
          {gap > 0 ? '(الفاتورة أعلى)' : '(الفاتورة أقل)'} — راجِع السبب، ولا تُعدّل الحساب ليطابقها.
        </Alert>
      )}
      {gap === 0 && <div className="obw-gap obw-gap--match">الفاتورة مطابقة لحسابنا.</div>}

      {canManage && (
        <button type="button" className="obw-reopen" onClick={onReopen} disabled={busy}>
          إعادة فتح الشهر
        </button>
      )}
    </section>
  );
};

const BillingProfileModal = ({ show, owner, busy, onHide, onSave }) => {
  const [form, setForm] = useState({
    legalName: '', customerType: '', vatRegistered: '', vatNumber: '', ownerSourcedRate: ''
  });

  useEffect(() => {
    if (!show || !owner) return;
    setForm({
      // ⚠️ `legalName`, NOT `billedName`. The latter falls back to the account name for
      // display; seeding it here and pressing save would persist a legal name nobody
      // typed — turning "not recorded" into a value that then freezes onto every record.
      legalName: owner.legalName || '',
      customerType: owner.customerType || '',
      vatRegistered: owner.vatRegistered === null ? '' : String(owner.vatRegistered),
      vatNumber: owner.vatNumber || '',
      // Stored as a FRACTION (0.15), typed as a PERCENTAGE (15). The conversion happens
      // here and on save — nowhere else, so there is one place to be wrong.
      // ×100 with a 4-dp guard against float noise (0.15 × 100 = 15.000000000000002).
      // NOT a round to 1 dp: that re-saved a stored 0.1234 as 0.123 when someone opened
      // this modal to fix an unrelated field.
      ownerSourcedRate: owner.ownerSourcedCommissionRate == null
        ? ''
        : String(Math.round(owner.ownerSourcedCommissionRate * 1000000) / 10000)
    });
  }, [show, owner]);

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  /**
   * The typed rate, parsed once. `null` = the field is empty = "no agreement".
   *
   * ⚠️ `invalid` must BLOCK the save, never fall through. `Number('١٥')` and
   * `Number('15%')` are NaN, and `JSON.stringify(NaN)` is `null` — which is exactly the
   * value that REMOVES an agreement. Left to itself, a typo would silently delete the
   * owner's negotiated rate and answer 200.
   */
  const rateText = String(form.ownerSourcedRate).trim();
  const rateInvalid = rateText !== '' && !Number.isFinite(Number(rateText));
  const rateFraction = rateText === '' ? null : Number(rateText) / 100;
  // Mirrors `isValidOwnerSourcedRate` on the backend: only ever reduces, never zero.
  const rateOutOfRange = !rateInvalid && rateFraction !== null && (rateFraction <= 0 || rateFraction > 0.25);

  return (
    <Modal show={show} onHide={onHide} centered dir="rtl">
      <Modal.Header closeButton><Modal.Title>الملف الضريبي للعميل</Modal.Title></Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>الاسم على الفاتورة</Form.Label>
          <Form.Control value={form.legalName} onChange={set('legalName')} />
          <Form.Text>قد يختلف عن اسم الحساب.</Form.Text>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>نوع العميل</Form.Label>
          <Form.Select value={form.customerType} onChange={set('customerType')}>
            <option value="">غير محدد</option>
            <option value="individual">{CUSTOMER_TYPE_LABELS.individual}</option>
            <option value="business">{CUSTOMER_TYPE_LABELS.business}</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>مسجّل في ضريبة القيمة المضافة</Form.Label>
          <Form.Select value={form.vatRegistered} onChange={set('vatRegistered')}>
            <option value="">لم يُسجَّل بعد</option>
            <option value="true">نعم</option>
            <option value="false">لا</option>
          </Form.Select>
        </Form.Group>
        {form.vatRegistered === 'true' && (
          <Form.Group className="mb-3">
            <Form.Label>الرقم الضريبي</Form.Label>
            <Form.Control value={form.vatNumber} onChange={set('vatNumber')} inputMode="numeric" />
            <Form.Text>15 رقمًا، يبدأ وينتهي بالرقم 3.</Form.Text>
          </Form.Group>
        )}

        {/* The ONLY negotiated money term in the system, and the only field on this
            form that changes what an owner is charged. Left blank = no agreement, which
            is every owner unless one has been signed — not 0%. */}
        <Form.Group>
          <Form.Label>نسبة الحجوزات التي يوفّرها المالك (%)</Form.Label>
          <Form.Control
            value={form.ownerSourcedRate}
            onChange={set('ownerSourcedRate')}
            inputMode="decimal"
            placeholder="لا يوجد اتفاق"
          />
          <Form.Text>
            تُطبَّق فقط على الحجوزات المُسجَّلة يدويًا كحجز «من المالك». اتركه فارغًا إذا لم يوجد
            اتفاق — الحجوزات حينها تُحتسب بالنسبة الأساسية.
          </Form.Text>
          {rateInvalid && (
            <Form.Text className="text-danger d-block">
              أدخل رقمًا صحيحًا بالأرقام اللاتينية (مثال: 15).
            </Form.Text>
          )}
          {rateOutOfRange && (
            <Form.Text className="text-danger d-block">
              النسبة يجب أن تكون أكبر من صفر ولا تتجاوز 25%.
            </Form.Text>
          )}
        </Form.Group>

        {/* The per-OTA VAT registration rows that lived here moved to the page's own
            «حسابات المنصات والتسجيل الضريبي» box (OtaAccountsSection): one date per platform
            had room for ONE Airbnb account, and saving this form wiped a second. This form
            no longer sends `otaVatRegistration`, so the stored value is left untouched until
            it is retired (tasks/pricing-listing-build-brainstorm.md, slice 1b). */}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={busy}>إلغاء</Button>
        <Button
          variant="primary"
          disabled={busy || rateInvalid || rateOutOfRange}
          onClick={() => onSave({
            legalName: form.legalName || null,
            customerType: form.customerType || null,
            vatRegistered: form.vatRegistered === '' ? null : form.vatRegistered === 'true',
            vatNumber: form.vatNumber || null,
            // Blank → null ("no agreement"), never 0. Unparseable never reaches here —
            // the button is disabled — because NaN would serialise to null and clear the
            // agreement instead of failing.
            ownerSourcedCommissionRate: rateFraction
          })}
        >
          حفظ
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

/**
 * Finalising freezes the figures. The confirmation states that plainly, and surfaces
 * any unbilled rows BEFORE the click — an unpriced booking is revenue that will never
 * be charged, and after finalisation the only way back is a re-open.
 */
const FinaliseModal = ({ show, month, total, hasBlockingWarnings, unresolvedRefs = [], busy, onHide, onConfirm }) => {
  const [discountOn, setDiscountOn] = useState(false);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (show) { setDiscountOn(false); setAmount(''); setReason(''); }
  }, [show]);

  const discountReady = !discountOn || (Number(amount) > 0 && reason.trim().length > 0);

  return (
    <Modal show={show} onHide={onHide} centered dir="rtl">
      <Modal.Header closeButton><Modal.Title>اعتماد {formatBusinessMonthLatn(month)}</Modal.Title></Modal.Header>
      <Modal.Body>
        <p className="obw-modal-lead">
          سيتم تثبيت الأرقام كما هي الآن. بعد الاعتماد لن تتغيّر مع تغيّر بيانات الحجوزات،
          ولا يمكن تعديلها — يمكن فقط إعادة فتح الشهر.
        </p>

        {/* Two different situations, two different sentences. An unresolved VAT treatment
            BLOCKS — the figure exists and nobody has established it. A pending row (a
            Gathern key-in) only warns — freezing now means not billing it, which may be
            the intent. */}
        {unresolvedRefs.length > 0 && (
          <Alert variant="danger">
            {FINALISE_BLOCKED_NOTE}
            <div className="obw-modal-refs">{unresolvedRefs.join('، ')}</div>
          </Alert>
        )}
        {hasBlockingWarnings && unresolvedRefs.length === 0 && (
          <Alert variant="danger">
            يوجد حجز أو أكثر بدون قيمة محتسبة. اعتماد الشهر الآن يعني عدم فوترة هذا الإيراد.
          </Alert>
        )}

        <div className="obw-modal-total">
          الإجمالي قبل الخصم: <strong>{money(total)}</strong>
        </div>

        <Form.Check
          type="checkbox"
          id="obw-discount-toggle"
          label="إضافة خصم لهذا الشهر"
          checked={discountOn}
          onChange={(e) => setDiscountOn(e.target.checked)}
          className="obw-modal-check"
        />

        {discountOn && (
          <>
            <Form.Group className="mb-2">
              <Form.Label>مبلغ الخصم</Form.Label>
              <Form.Control
                type="number" step="0.01" min="0" inputMode="decimal"
                value={amount} onChange={(e) => setAmount(e.target.value)}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>السبب</Form.Label>
              <Form.Control value={reason} onChange={(e) => setReason(e.target.value)} />
              <Form.Text>يُحفظ مع السجل — الخصم سطر مستقل، ولا يغيّر نسبة العمولة.</Form.Text>
            </Form.Group>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={busy}>إلغاء</Button>
        <Button
          variant="primary"
          disabled={busy || !discountReady || unresolvedRefs.length > 0}
          onClick={() => onConfirm(discountOn ? { amount: Number(amount), reason: reason.trim() } : undefined)}
        >
          اعتماد
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const ReopenModal = ({ show, hasInvoice, busy, onHide, onConfirm }) => {
  const [reason, setReason] = useState('');
  useEffect(() => { if (show) setReason(''); }, [show]);

  return (
    <Modal show={show} onHide={onHide} centered dir="rtl">
      <Modal.Header closeButton><Modal.Title>إعادة فتح الشهر</Modal.Title></Modal.Header>
      <Modal.Body>
        <p className="obw-modal-lead">
          يبقى السجل الحالي في السجلّات كما هو، ويعود الشهر لحالة المسودة ليُعاد اعتماده.
        </p>
        {hasInvoice && (
          <Alert variant="warning">
            سبق أن سجّل المحاسب فاتورة على هذا الشهر. أعِد فتحه إن كانت أرقامنا بحاجة إلى تصحيح،
            وأبلِغ المحاسب — تصحيح فاتورتهم يتم من نظامهم.
          </Alert>
        )}
        <Form.Group>
          <Form.Label>السبب</Form.Label>
          <Form.Control value={reason} onChange={(e) => setReason(e.target.value)} />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={busy}>إلغاء</Button>
        <Button variant="danger" disabled={busy || !reason.trim()} onClick={() => onConfirm(reason.trim())}>
          إعادة الفتح
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default OwnerBillingWorksheet;
